import React, { useState, useRef, useEffect } from 'react';
import ChatReactionButton from './ChatReactionButton';
import { ReactComponent as FileIcon } from "../../images/svg/file-icon-black.svg";
import { useSelector } from 'react-redux';
import { ReactComponent as ThumbsUpIcon } from "../../images/svg/like.svg";
import { ReactComponent as HeartIcon } from "../../images/svg/heart.svg";
import { ReactComponent as GrinIcon } from "../../images/svg/haha.svg";
import { ReactComponent as WowIcon } from "../../images/svg/wow.svg";
import { ReactComponent as ClappingIcon } from "../../images/svg/clapping.svg";
import { ReactComponent as ArrowUpIcon } from "../../images/svg/down_white.svg";

const ChatMessage = ({ message, isSelf, getAvatarColor, onReply, onReaction, allMessages = [] }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [showReactionDetails, setShowReactionDetails] = useState(false);
  const dropdownRef = useRef(null);
  const dropdownButtonRef = useRef(null);
  const reactionDetailsRef = useRef(null);
  const reactionButtonRef = useRef(null);
  const messageAreaRef = useRef(document.getElementById('messageArea'));

  const reactionComponents = {
    'like': ThumbsUpIcon,
    'love': HeartIcon,
    'haha': GrinIcon,
    'clap': ClappingIcon,
    'wow': WowIcon,
  };

  const scrollToBottom = () => {
    if (messageAreaRef.current) {
      const scrollHeight = messageAreaRef.current.scrollHeight;
      const height = messageAreaRef.current.clientHeight;
      const maxScrollTop = scrollHeight - height;
      messageAreaRef.current.scrollTop = maxScrollTop > 0 ? maxScrollTop : 0;
    }
  };

  const repliedToMessage = message.parentMessageId && allMessages 
    ? allMessages.find(m => m.messageId === message.parentMessageId)
    : null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        reactionDetailsRef.current && 
        !reactionDetailsRef.current.contains(event.target) &&
        !reactionButtonRef.current.contains(event.target)
      ) {
        setShowReactionDetails(false);
      }
      
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        !dropdownButtonRef.current.contains(event.target)
      ) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (showDropdown) {
      setTimeout(scrollToBottom, 50);
    }
  }, [showDropdown]);

  const handleCopy = () => {
    const content = typeof message.content === 'object' 
        ? JSON.stringify(message.content) 
        : message.content;
    navigator.clipboard.writeText(content);
    setShowDropdown(false);
  };

  const handleReply = () => {
    onReply(message.messageId);
    setShowDropdown(false);
  };

  const handleDropdownToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setShowDropdown(!showDropdown);
  };

  const getReplyIndicatorText = () => {
    if (!repliedToMessage) return '';
    
    const isSelfReply = message.sender === repliedToMessage.sender;
    if (isSelf) {
      return isSelfReply ? "You replied to yourself" : `You replied to ${repliedToMessage.name}`;
    }
    return isSelfReply ? 
      `${message.name} replied to themselves` : 
      `${message.name} replied to ${repliedToMessage.name}`;
  };

  const renderMessageContent = (text) => {
    if (message.isReaction && reactionComponents[message.content]) {
      const EmojiComponent = reactionComponents[message.content];
      return <EmojiComponent width="20" height="20" />;
    }
    
    const urlRegex = /(https?:\/\/[^\s]+)|(www\.[^\s]+)/g;
    
    if (!text) return text;
    
    // Convert the input to string to handle numbers
    const textString = String(text);
    
    // if there is any url in the message, make it clickable
    const parts = textString.split(urlRegex);
    
    return parts.map((part, index) => {
      if (!part) return null;
      
      if (urlRegex.test(part)) {
        const href = part.startsWith('www.') ? `https://${part}` : part;
        return (
          <a 
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="message-link"
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  function formatTimeToLocal(timestamp) {
    const [hours, minutes, seconds] = timestamp.split(':');
    const utcDate = new Date();
    utcDate.setUTCHours(hours, minutes, seconds);
    return utcDate.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
    });
  }

  const handleReaction = (reactionKey) => {
    onReaction(message.messageId, reactionKey);
  };

  const getEmojiForReaction = (reaction) => {
    const IconComponent = reactionComponents[reaction];
    return IconComponent ? <IconComponent width="16" height="16" /> : reaction;
  };

  const [tooltipContent, setTooltipContent] = useState(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleDownload = () => {
    if (message.content.downloadURL) {
      window.open(message.content.downloadURL, '_blank');
    }
    setShowDropdown(false);
  };

  const renderMessage = (content) => {
    if (typeof content === 'object') {
        if (content.type === 'FILE') {
            return (
                <div className="file-message">
                    <FileIcon className="file-icon" width="16" height="16" />
                    <span className="file-name">{content.fileName}</span>
                </div>
            );
        } else if (content.type === 'REACTION') {
            const IconComponent = reactionComponents[content.reaction];
            return IconComponent ? <IconComponent width="20" height="20" /> : content.reaction;
        }
    }
    
    return renderMessageContent(content);
  };

  const renderRepliedContent = (content) => {
    if (!content) return '';
    
    if (typeof content === 'object') {
      if (content.type === 'FILE') {
        return `File: ${content.fileName}`;
      }
      return JSON.stringify(content);
    }
    return content;
  };

  const renderReactions = (reactions) => {
    if (!reactions || reactions.length === 0) return null;
    
    const latestUserReactions = reactions.reduce((acc, reaction) => {
      acc[reaction.sender] = reaction;
      return acc;
    }, {});

    const groupedReactions = Object.values(latestUserReactions).reduce((acc, reaction) => {
      const key = reaction.reaction;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(reaction);
      return acc;
    }, {});

    const sortedReactionTypes = Object.entries(groupedReactions)
      .sort(([, usersA], [, usersB]) => usersB.length - usersA.length)
      .map(([type]) => type);

    const totalReactions = Object.values(latestUserReactions).length;

    return (
      <div className="reaction-summary">
        <button 
          ref={reactionButtonRef}
          className="reaction-group-bubble"
          onClick={() => setShowReactionDetails(!showReactionDetails)}
        >
          <div className="reaction-icons">
            {sortedReactionTypes.slice(0, 3).map(type => (
              <span key={type} className="reaction-emoji">
                {getEmojiForReaction(type)}
              </span>
            ))}
          </div>
          <span className="reaction-total-count">{totalReactions}</span>
        </button>

        {showReactionDetails && (
          <div className="reaction-details-popup" ref={reactionDetailsRef}>
            {sortedReactionTypes.map(type => (
              <div key={type} className="reaction-detail-item">
                <div className="reaction-users-list">
                  {groupedReactions[type].map(user => (
                    <div key={user.sender} className="reaction-user-row">
                      <span className="reaction-emoji">{getEmojiForReaction(type)}</span>
                      <span className="reaction-user-name">{user.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const disableChat = useSelector(state => state.participants.disableChat);

  return (
    <li className={isSelf ? "chat-message-self" : "chat-message"}>
      {!isSelf && (
        <div className="chat-display-name">
          <i style={{backgroundColor: getAvatarColor(message.sender)}}>
            {message.name?.charAt(0) || '?'}
          </i>
          <span>{message.name || 'Unknown'}</span>
        </div>
      )}
      <div className="chat-individual-message">
        {message.parentMessageId && repliedToMessage && (
          <>
            <div className="reply-indicator">
              {getReplyIndicatorText()}
            </div>
            <div className="replied-message">
              <div className="replied-message-sender">{repliedToMessage.name}</div>
              <div className="replied-message-text">
                {renderRepliedContent(repliedToMessage.content)}
              </div>
            </div>
          </>
        )}
        <div className="message-text">
          {renderMessage(message.content)}
        </div>
        {renderReactions(message.reactions)}

        {tooltipContent && (
          <div 
            className="reaction-users-tooltip"
            style={{
              left: tooltipPosition.x,
              top: tooltipPosition.y
            }}
          >
            {tooltipContent}
          </div>
        )}
        
        <div className="message-timestamp">
          {formatTimeToLocal(message.timestamp)}
        </div>
        <div className="message-actions">
          {!disableChat && <ChatReactionButton onReactionSelect={handleReaction} />}
          <button
            ref={dropdownButtonRef}
            className="message-dropdown-trigger"
            onClick={handleDropdownToggle}
          >
            <ArrowUpIcon />
          </button>
        </div>
        {showDropdown && (
          <div className="message-dropdown-menu" ref={dropdownRef}>
            {!disableChat && typeof message.content === 'string' && <button onClick={handleReply}>Reply</button>}
            {message.content.type === 'FILE' ? (
              <button onClick={handleDownload}>Download</button>
            ) : (
              <button onClick={handleCopy}>Copy</button>
            )}
          </div>
        )}
      </div>
    </li>
  );
};

export default ChatMessage;